import { MessageSquare, Calendar, Clipboard, User, FileText, Loader2 } from 'lucide-react';
import { Card, CardContent, CardFooter, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useState } from 'react';
import { startConversationWithPatient } from '@/services/chat';

interface Patient {
  id: string;
  userId: string;
  user?: {
    name: string;
    email: string;
  };
  cpf?: string;
  phone?: string;
  lastEncounter?: string;
  lastAppointmentId?: string | null;
  lastAppointmentStatus?: string | null;
}

interface PatientCardProps {
  patient: Patient;
}

export function PatientCard({ patient }: PatientCardProps) {
  const navigate = useNavigate();
  const [isOpeningChat, setIsOpeningChat] = useState(false);
  const patientName = patient.user?.name || 'Paciente sem nome';
  const patientKey = patient.userId || patient.id;

  const openConsultationRoom = async () => {
    if (isOpeningChat) return;

    // Caminho rápido: já temos appointmentId canônico da listagem
    if (patient.lastAppointmentId) {
      if (import.meta.env.DEV) {
        console.debug('[MeusPacientes] Abrindo chat com lastAppointmentId', {
          patientId: patientKey,
          appointmentId: patient.lastAppointmentId,
        });
      }
      navigate(`/chat/${patient.lastAppointmentId}`, {
        state: { from: '/pacientes' },
      });
      return;
    }

    if (!patientKey) {
      toast.error('Não foi possível identificar o paciente para abrir o chat.');
      return;
    }

    setIsOpeningChat(true);
    try {
      if (import.meta.env.DEV) {
        console.debug('[MeusPacientes] Resolvendo thread via start-conversation', {
          patientId: patientKey,
        });
      }

      const result = await startConversationWithPatient(patientKey);
      if (!result.success || !result.data?.appointmentId) {
        toast.error(
          result.error ||
            'Nenhuma consulta encontrada para abrir o chat com este paciente.',
        );
        return;
      }

      navigate(`/chat/${result.data.appointmentId}`, {
        state: { from: '/pacientes' },
      });
    } catch (error) {
      console.error('[MeusPacientes] Falha ao abrir chat', error);
      toast.error('Não foi possível abrir o chat. Tente novamente.');
    } finally {
      setIsOpeningChat(false);
    }
  };

  return (
    <Card className="hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200 bg-white dark:bg-neutral-900 border border-gray-100 dark:border-neutral-800 overflow-hidden">
      <CardHeader className="pb-2">
        <div className="flex justify-between items-start">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-teal-500 to-emerald-600 flex items-center justify-center text-white font-bold text-lg shadow-sm">
              {patientName.charAt(0).toUpperCase()}
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-white leading-tight">{patientName}</h3>
              <p className="text-xs text-gray-500 dark:text-gray-400">CPF: {patient.cpf || 'Não informado'}</p>
            </div>
          </div>
          <Badge className="text-[10px] py-0 px-2 bg-emerald-100 text-emerald-700 hover:bg-emerald-200 border-0 dark:bg-emerald-900/30 dark:text-emerald-400 dark:hover:bg-emerald-900/50">
            Ativo
          </Badge>
        </div>
      </CardHeader>

      <CardContent className="py-2">
        <div className="space-y-1">
          <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300">
            <User className="w-3.5 h-3.5 opacity-70" />
            <span>ID: {patient.id.substring(0, 8)}...</span>
          </div>
          {patient.phone && (
            <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300">
              <span className="font-medium opacity-70">Tel:</span> {patient.phone}
            </div>
          )}
          {patient.lastEncounter && (
            <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300 mt-1.5 pt-1.5 border-t border-gray-50 dark:border-neutral-800">
              <Calendar className="w-3.5 h-3.5 opacity-70" />
              <span>Última consulta: {new Date(patient.lastEncounter).toLocaleDateString('pt-BR')}</span>
            </div>
          )}
        </div>
      </CardContent>

      <CardFooter className="pt-3 pb-4 px-4 grid grid-cols-2 gap-2 bg-gray-50/50 dark:bg-neutral-900/50 border-t border-gray-100/50 dark:border-neutral-800/50 mt-2">
        <Button
          variant="outline"
          size="sm"
          className="w-full gap-2 text-blue-600 border-blue-200 bg-white hover:bg-blue-50 dark:bg-transparent dark:border-blue-900/50 dark:hover:bg-blue-900/20"
          onClick={openConsultationRoom}
          disabled={isOpeningChat}
        >
          {isOpeningChat ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <MessageSquare className="w-3.5 h-3.5" />
          )}
          <span className="text-xs">Chat</span>
        </Button>

        <Button
          variant="outline"
          size="sm"
          className="w-full gap-2 text-purple-600 border-purple-200 bg-white hover:bg-purple-50 dark:bg-transparent dark:border-purple-900/50 dark:hover:bg-purple-900/20"
          onClick={() => navigate(`/prontuario/${patient.id}`)}
        >
          <Clipboard className="w-3.5 h-3.5" />
          <span className="text-xs">Prontuário</span>
        </Button>

        <Button
          variant="outline"
          size="sm"
          className="w-full gap-2 text-teal-600 border-teal-200 bg-white hover:bg-teal-50 dark:bg-transparent dark:border-teal-900/50 dark:hover:bg-teal-900/20"
          onClick={() => navigate(`/agendamento?patientId=${patient.userId}`)}
        >
          <Calendar className="w-3.5 h-3.5" />
          <span className="text-xs">Agendar</span>
        </Button>

        <Button
          variant="outline"
          size="sm"
          className="w-full gap-2 text-indigo-600 border-indigo-200 bg-white hover:bg-indigo-50 dark:bg-transparent dark:border-indigo-900/50 dark:hover:bg-indigo-900/20"
          onClick={openConsultationRoom}
          disabled={isOpeningChat}
        >
          <FileText className="w-3.5 h-3.5" />
          <span className="text-xs">Documentos</span>
        </Button>
      </CardFooter>
    </Card>
  );
}
